import {
  CreateEditDtoSourceType,
  EnrichedVideoEditorEditListItemDto,
} from '@app/modules/fragments/fragment.types';
import {
  CloneVideoEditionEditDialog,
  useVideoEditionQuickCreate,
  VideoEditionsEditList,
  VideoEditionsEditListSkeleton,
  VideoEditionsEditsEmptyState,
} from '@app/modules/video-editions';
import { getCompletedPublishedPlatformsSummary } from '@app/modules/video-editions/lib/get-completed-published-platforms-summary';
import { webAppListEditsVideoEditorEditsQueryKey } from '@mi-front/api-v2';
import { useQueryClient } from '@tanstack/react-query';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { useState } from 'react';
import { useFragment } from '../../hooks/useFragment';
import { useFragmentVideoEditionsEditsQuery } from '../../hooks/useFragmentVideoEditionsEditsQuery';
import { FragmentVideoEditionPublishMenu } from '../social-export-panorama/components/FragmentVideoEditionPublishMenu';
import { useFragmentSocialUploadsQuery } from '../social-export-panorama/hooks/useFragmentSocialUploadsQuery';

/**
 * Tab content for video editions: lists edits, chip navigation, and clone/create/delete flows.
 */
export function FragmentVideoEditionsTab() {
  const { fragment } = useFragment();
  const queryClient = useQueryClient();
  const [cloneEdit, setCloneEdit] = useState<EnrichedVideoEditorEditListItemDto | null>(null);
  const uploadsQuery = useFragmentSocialUploadsQuery(fragment.id);
  const listQuery = useFragmentVideoEditionsEditsQuery(fragment.id);

  const { createWithAutoName, isCreating } = useVideoEditionQuickCreate({
    buildCreatePayload: (name) => ({
      sourceType: CreateEditDtoSourceType.FRAGMENT,
      sourceId: fragment.id,
      name,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: webAppListEditsVideoEditorEditsQueryKey({ fragmentId: fragment.id }),
      });
    },
  });

  const invalidateEdits = async () => {
    await queryClient.invalidateQueries({
      queryKey: webAppListEditsVideoEditorEditsQueryKey({ fragmentId: fragment.id }),
    });
  };

  return (
    <>
      <QueryView query={listQuery} loader={<VideoEditionsEditListSkeleton />}>
        {(editsFromQuery) =>
          !editsFromQuery.length ? (
            <VideoEditionsEditsEmptyState
              createWithAutoName={createWithAutoName}
              isCreating={isCreating}
            />
          ) : (
            <VideoEditionsEditList
              edits={editsFromQuery}
              onCloneEdit={(edit) => setCloneEdit(edit)}
              onEditMutationSuccess={invalidateEdits}
              renderPublishControl={(edit) => {
                const { count: completedPublishCount } = getCompletedPublishedPlatformsSummary(
                  edit.publishedPlatforms,
                );
                return (
                  <FragmentVideoEditionPublishMenu
                    editId={edit.id}
                    fragmentId={fragment.id}
                    uploadsByPlatform={uploadsQuery.data}
                    iconOnly={completedPublishCount > 0}
                  />
                );
              }}
              createWithAutoName={createWithAutoName}
              isCreating={isCreating}
            />
          )
        }
      </QueryView>

      {cloneEdit && (
        <CloneVideoEditionEditDialog
          open={!!cloneEdit}
          onClose={() => setCloneEdit(null)}
          sourceEdit={cloneEdit}
          onSuccess={invalidateEdits}
        />
      )}
    </>
  );
}
